import { useState, useEffect, useMemo } from "react";

/**
 * Breakpoint → column count mapping for masonry layout.
 * Must match the responsive breakpoints in tailwind.config.ts
 */
const COLUMN_BREAKPOINTS: Array<{ minWidth: number; columns: number }> = [
  // Page frames have a bounded width, even on an ultrawide monitor.
  { minWidth: 1536, columns: 6 },
  { minWidth: 1280, columns: 5 },
  { minWidth: 1024, columns: 4 },
  { minWidth: 768, columns: 3 },
  { minWidth: 640, columns: 2 },
  { minWidth: 0, columns: 1 },
];

function getColumnCount(width: number): number {
  for (const bp of COLUMN_BREAKPOINTS) {
    if (width >= bp.minWidth) {
      return bp.columns;
    }
  }
  return 1;
}

/**
 * Returns current column count based on viewport width.
 * Updates on resize.
 */
export function useMasonryColumns(): number {
  const [columns, setColumns] = useState(() =>
    typeof window !== "undefined" ? getColumnCount(window.innerWidth) : 1
  );

  useEffect(() => {
    const handleResize = () => {
      setColumns(getColumnCount(window.innerWidth));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return columns;
}

/**
 * Distributes items into columns using "shortest column first" algorithm.
 * Items are placed left-to-right into whichever column is currently shortest.
 *
 * Returns an array of columns, each containing the items for that column.
 * Items stay stable once placed - appending new items doesn't shift existing ones.
 */
export function useDistributeToColumns<T extends { id: string }>(
  items: T[],
  columnCount: number,
  getItemHeight: (item: T) => number
): T[][] {
  return useMemo(() => {
    if (columnCount <= 0) return [];
    if (items.length === 0) return Array.from({ length: columnCount }, () => []);

    // Initialize columns and their heights
    const columns: T[][] = Array.from({ length: columnCount }, () => []);
    const heights: number[] = Array(columnCount).fill(0);

    // Place each item in the shortest column
    for (const item of items) {
      // Find shortest column (prefer left on tie for L→R reading)
      let shortestIdx = 0;
      let shortestHeight = heights[0];

      for (let i = 1; i < columnCount; i++) {
        if (heights[i] < shortestHeight) {
          shortestHeight = heights[i];
          shortestIdx = i;
        }
      }

      // Add item to shortest column
      columns[shortestIdx].push(item);
      heights[shortestIdx] += getItemHeight(item);
    }

    return columns;
  }, [items, columnCount, getItemHeight]);
}
